import { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../../shared/components/AppLoader";
import { MarkdownView } from "../../shared/components/MarkdownView/MarkdownView";
import { getRoutePromptsRevision } from "../../shared/const/router";
import { PromptChangeSummary } from "./PromptChangeSummary";
import { ResultsChangeSummary } from "./ResultsChangeSummary";
import {
  formatInputCount,
  getComparison,
  setComparisonVerdict,
  type ComparisonDetail,
  type TestRun,
  type TestVerdict
} from "../../shared/services/promptTests";

const VERDICTS: { value: TestVerdict; label: string }[] = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "inconclusive", label: "Inconclusive" }
];

function isRunning(run: TestRun): boolean {
  return run.status === "pending" || run.status === "running";
}

// Header, metrics, project meta, final brief and the transcript label each take one shared grid row,
// then one row per transcript turn, so both columns line up section by section and turn by turn.
const FIXED_ROWS = 5;

function spanRows(count: number) {
  return { gridRow: `span ${count} / span ${count}` };
}

const RunColumn: FC<{ title: string; subtitle: string; run: TestRun; rowCount: number; raw: boolean }> = ({
  title,
  subtitle,
  run,
  rowCount,
  raw
}) => {
  return (
    <div className="grid grid-rows-subgrid rounded-lg border bg-card" style={spanRows(rowCount)}>
      <div className="border-b bg-gray-50 px-4 py-3">
        <div className="text-[14px] font-medium text-gray-900">{title}</div>
        <div className="text-[12px] text-gray-400">{subtitle}</div>
      </div>

      {isRunning(run) ? (
        <div className="flex flex-col items-center gap-2 px-4 py-10" style={spanRows(rowCount - 1)}>
          <AppLoader />
          <div className="text-[13px] text-gray-500">{run.phase ?? "Starting…"}</div>
          {run.turnsCompleted > 0 && (
            <div className="text-[12px] text-gray-400">{formatInputCount(run.turnsCompleted)} replayed</div>
          )}
        </div>
      ) : run.status === "failed" ? (
        <div className="px-4 pb-3" style={spanRows(rowCount - 1)}>
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {run.error ?? "Run failed"}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 px-4 text-[12px] text-gray-500">
            <span>
              Completeness: <span className="text-gray-900">{run.completeness?.overallScore ?? "—"}</span>
            </span>
            <span>Ready: {run.completeness?.readyToAdvance ? "yes" : "no"}</span>
            <span>Tokens: {(run.inputTokens ?? 0) + (run.outputTokens ?? 0)}</span>
            <span>Latency: {run.latencyMs ? `${Math.round(run.latencyMs / 1000)}s` : "—"}</span>
          </div>

          <div className="px-4">
            <div className="h-full rounded border border-gray-200 px-3 py-2">
              <div className="text-[12px] uppercase tracking-wide text-gray-400">Project meta</div>
              <div className="text-[13px] text-gray-900">{run.projectMeta?.name ?? "—"}</div>
              <div className="text-[12px] text-gray-500">{run.projectMeta?.description ?? ""}</div>
            </div>
          </div>

          <div className="flex flex-col px-4">
            <div className="mb-1 text-[12px] uppercase tracking-wide text-gray-400">Final brief</div>
            <div className="max-h-[360px] flex-1 overflow-auto rounded border border-gray-200 bg-gray-50 px-3 py-2">
              {raw ? (
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-[16px] text-gray-800">
                  {run.finalBrief ?? "—"}
                </pre>
              ) : (
                <MarkdownView content={run.finalBrief ?? "—"} />
              )}
            </div>
          </div>

          <div className="px-4 text-[12px] uppercase tracking-wide text-gray-400 last:pb-3">Transcript</div>

          {(run.transcript ?? []).map((turn) => (
            <div key={turn.index} className="px-4 last:pb-3">
              <div className="h-full rounded border border-gray-200 px-3 py-2">
                <div className="text-[13px] text-gray-900">
                  <span className="text-gray-400">User:</span> {turn.userText}
                </div>
                <div className="mt-1 text-[13px] text-gray-700">
                  <span className="text-gray-400">Assistant:</span>{" "}
                  {raw ? turn.assistantText : <MarkdownView content={turn.assistantText} className="inline-block" />}
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  <span>intent: {turn.intent}</span>
                  {turn.briefChanged && <span className="text-blue-600">brief updated</span>}
                  {turn.tools.length > 0 && <span>tools: {turn.tools.join(", ")}</span>}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export const ComparisonPage: FC = () => {
  const { featureKey = "", revisionId = "", comparisonId = "" } = useParams<{
    featureKey: string;
    revisionId: string;
    comparisonId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [verdict, setVerdict] = useState<TestVerdict | null>(null);
  const [note, setNote] = useState("");
  const [raw, setRaw] = useState(false);

  const queryKey = ["prompt-comparison", comparisonId];

  const { data, isLoading, isError } = useQuery<ComparisonDetail>({
    queryKey,
    queryFn: () => getComparison(Number(comparisonId)),
    enabled: !!comparisonId,
    refetchInterval: (query) => {
      const c = query.state.data as ComparisonDetail | undefined;
      if (!c) return 3000;
      return isRunning(c.baselineRun) || isRunning(c.candidateRun) ? 3000 : false;
    }
  });

  useEffect(() => {
    setVerdict(data && data.verdict !== "pending" ? data.verdict : null);
    setNote(data?.verdictNote ?? "");
  }, [data?.verdict, data?.verdictNote]);

  const saveVerdict = useMutation({
    mutationFn: (payload: { verdict: TestVerdict; note: string | null }) =>
      setComparisonVerdict(Number(comparisonId), payload.verdict, payload.note),
    onSuccess: () => {
      toast.success("Verdict saved");
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["prompt-revision-comparisons", Number(revisionId)] });
      queryClient.invalidateQueries({ queryKey: ["admin-prompt-revision", revisionId] });
      navigate(getRoutePromptsRevision(featureKey, Number(revisionId)));
    },
    onError: () => toast.error("Failed to save the verdict")
  });

  if (isLoading) return <AppLoader />;
  if (isError || !data) return <div className="p-8 text-center text-red-600">Failed to load the comparison.</div>;

  const savedVerdict = data.verdict === "pending" ? null : data.verdict;
  const isDirty = verdict !== savedVerdict || note.trim() !== (data.verdictNote ?? "");
  const rowCount =
    FIXED_ROWS +
    Math.max(data.baselineRun.transcript?.length ?? 0, data.candidateRun.transcript?.length ?? 0);

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="mb-6">
        <Link
          to={getRoutePromptsRevision(featureKey, Number(revisionId))}
          className="text-[14px] text-gray-500 hover:text-gray-700"
        >
          ← Back to revision {data.candidateRun.revisionNumber ?? data.candidateRun.revisionId}
        </Link>
        <FigmaTypography component="h1" variant="heading-1" className="mt-2">
          {data.testCaseVersion.testCase.name} · v{data.testCaseVersion.version}
        </FigmaTypography>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (verdict) saveVerdict.mutate({ verdict, note: note.trim() || null });
        }}
        className="mb-6 rounded-lg border bg-card px-6 py-4"
      >
        <FigmaTypography component="div" variant="body-2" className="text-gray-900">
          Verdict
        </FigmaTypography>
        <select
          value={verdict ?? ""}
          onChange={(e) => setVerdict(e.target.value ? (e.target.value as TestVerdict) : null)}
          className="mt-3 w-full rounded border border-gray-200 px-3 py-2 text-[13px] text-gray-800 outline-hidden focus:ring-1 focus:ring-gray-300"
        >
          <option value="">Select a verdict…</option>
          {VERDICTS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why did you reach this verdict?"
          className="mt-3 h-20 w-full resize-y rounded border border-gray-200 px-3 py-2 text-[13px] text-gray-800 outline-hidden focus:ring-1 focus:ring-gray-300"
        />
        <div className="mt-3 flex items-center gap-3">
          <AppButton
            type="submit"
            disabled={!verdict || !isDirty}
            isLoading={saveVerdict.isPending}
            loadingText="Saving..."
          >
            Save verdict
          </AppButton>
          {isDirty ? (
            <span className="text-[12px] text-amber-600">Unsaved changes</span>
          ) : savedVerdict ? (
            <span className="text-[12px] text-gray-500">Saved as {savedVerdict}</span>
          ) : (
            <span className="text-[12px] text-gray-400">Not recorded yet</span>
          )}
        </div>
      </form>

      <PromptChangeSummary
        candidateRevisionId={data.candidateRun.revisionId}
        baselineRevisionId={data.baselineRun.revisionId}
      />

      {data.testCaseVersion.checks && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-6 py-4">
          <div className="mb-1 text-[12px] uppercase tracking-wide text-gray-400">What to check</div>
          <pre className="whitespace-pre-wrap font-sans text-[13px] leading-[20px] text-gray-700">
            {data.testCaseVersion.checks}
          </pre>
        </div>
      )}

      <ResultsChangeSummary
        comparisonId={data.id}
        enabled={data.baselineRun.status === "succeeded" && data.candidateRun.status === "succeeded"}
      />

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => setRaw((current) => !current)}
          className="text-[12px] text-gray-500 hover:text-gray-700"
        >
          {raw ? "Show rendered markdown" : "Show raw text"}
        </button>
      </div>

      <div
        className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2"
        style={{ gridTemplateRows: `repeat(${rowCount}, auto)` }}
      >
        <RunColumn
          title="Active revision (baseline)"
          subtitle={`Revision ${data.baselineRun.revisionNumber ?? data.baselineRun.revisionId}`}
          run={data.baselineRun}
          rowCount={rowCount}
          raw={raw}
        />
        <RunColumn
          title="Candidate revision"
          subtitle={`Revision ${data.candidateRun.revisionNumber ?? data.candidateRun.revisionId}`}
          run={data.candidateRun}
          rowCount={rowCount}
          raw={raw}
        />
      </div>
    </div>
  );
};
